/**
 * Project Management — roles, statuses, and shared helpers.
 * Used by project-service, project-permissions, and Task Management UI.
 */

import { TASK_ATTACHMENT_ACCEPT } from './task-config'

/** Reuse task module accepted MIME/extensions for member reference docs. */
export const PROJECT_MEMBER_ATTACHMENT_ACCEPT = TASK_ATTACHMENT_ACCEPT

/** Maximum size per member reference document (localStorage persistence). */
export const PROJECT_MEMBER_ATTACHMENT_MAX_BYTES = 1.5 * 1024 * 1024

export const PROJECT_ROLES = {
  PROJECT_MANAGER: 'project_manager',
  CREATOR: 'creator',
  REVIEWER: 'reviewer',
  CONTRIBUTOR: 'contributor',
}

export const PROJECT_ROLE_OPTIONS = [
  {
    key: PROJECT_ROLES.PROJECT_MANAGER,
    label: 'Project Manager',
    shortLabel: 'PM',
    variant: 'primary',
    icon: 'BriefcaseIcon',
    description: 'Full control — team, settings, and all tasks',
  },
  {
    key: PROJECT_ROLES.CREATOR,
    label: 'Task Creator',
    shortLabel: 'Creator',
    variant: 'info',
    icon: 'PlusCircleIcon',
    description: 'Creates and assigns tasks within the project',
  },
  {
    key: PROJECT_ROLES.REVIEWER,
    label: 'Reviewer',
    shortLabel: 'Reviewer',
    variant: 'warning',
    icon: 'EyeIcon',
    description: 'Reviews tasks and approves work in Review status',
  },
  {
    key: PROJECT_ROLES.CONTRIBUTOR,
    label: 'Contributor',
    shortLabel: 'Contributor',
    variant: 'success',
    icon: 'UserIcon',
    description: 'Works on assigned tasks and updates progress',
  },
]

export const PROJECT_STATUSES = {
  ACTIVE: 'Active',
  ON_HOLD: 'On Hold',
  COMPLETED: 'Completed',
  ARCHIVED: 'Archived',
}

export const PROJECT_STATUS_OPTIONS = [
  { key: PROJECT_STATUSES.ACTIVE, label: 'Active', variant: 'success' },
  { key: PROJECT_STATUSES.ON_HOLD, label: 'On Hold', variant: 'warning' },
  { key: PROJECT_STATUSES.COMPLETED, label: 'Completed', variant: 'info' },
  { key: PROJECT_STATUSES.ARCHIVED, label: 'Archived', variant: 'secondary' },
]

export const PROJECT_COLOR_OPTIONS = [
  '#7367F0', '#28C76F', '#FF9F43', '#EA5455', '#00CFE8', '#82868B', '#1E9FF2', '#FF6B6B',
]

export function projectId(project) {
  if (!project) return null
  return project.id ?? project.projectId ?? project.project_id ?? null
}

export function normalizeProjectStatus(project) {
  const raw = project?.status || PROJECT_STATUSES.ACTIVE
  const match = PROJECT_STATUS_OPTIONS.find(s => s.key.toLowerCase() === String(raw).toLowerCase())
  return match ? match.key : PROJECT_STATUSES.ACTIVE
}

export function projectStatusVariant(status) {
  const match = PROJECT_STATUS_OPTIONS.find(s => s.key === status)
  return match ? match.variant : 'secondary'
}

export function roleOption(roleKey) {
  return PROJECT_ROLE_OPTIONS.find(r => r.key === roleKey) || PROJECT_ROLE_OPTIONS[3]
}

export function roleLabel(roleKey) {
  return roleOption(roleKey).label
}

export function roleVariant(roleKey) {
  return roleOption(roleKey).variant
}

/** Map API / legacy role strings to canonical PROJECT_ROLES keys. */
export function normalizeProjectRole(role) {
  if (role == null || role === '') return PROJECT_ROLES.CONTRIBUTOR
  const raw = String(role).trim()
  const key = raw.toLowerCase().replace(/[\s-]+/g, '_')
  const byKey = PROJECT_ROLE_OPTIONS.find(r => r.key === key)
  if (byKey) return byKey.key
  const lower = raw.toLowerCase()
  const byLabel = PROJECT_ROLE_OPTIONS.find(
    r => r.label.toLowerCase() === lower || r.shortLabel.toLowerCase() === lower,
  )
  return byLabel ? byLabel.key : PROJECT_ROLES.CONTRIBUTOR
}

export function formatMemberFileSize(bytes) {
  if (bytes == null || Number.isNaN(Number(bytes))) return ''
  const n = Number(bytes)
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

/**
 * Normalize member attachment rows from API or localStorage.
 * Future API shape: { id, name, url?, filePath?, size?, uploadedAt, uploadedBy }
 */
export function normalizeMemberAttachments(attachments) {
  if (!Array.isArray(attachments)) return []
  return attachments.map((a, i) => {
    const url = a.url || a.filePath || a.file_path || null
    return {
      id: a.id || `att-${i}`,
      name: a.name || 'Document',
      url,
      filePath: a.filePath || a.file_path || null,
      size: a.size != null ? Number(a.size) : null,
      uploadedAt: a.uploadedAt || a.uploaded_at || null,
      uploadedBy: a.uploadedBy ?? a.uploaded_by ?? null,
    }
  }).filter(a => a.name && (a.url || a.filePath))
}

export function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

/**
 * Convert FileList/File[] into storable member attachments (base64 data URLs for local mode).
 */
export async function filesToMemberAttachments(files, { uploadedBy, maxBytes = PROJECT_MEMBER_ATTACHMENT_MAX_BYTES } = {}) {
  const attachments = []
  const errors = []
  const list = Array.from(files || [])
  for (const file of list) {
    if (!file) continue
    if (file.size > maxBytes) {
      errors.push(`"${file.name}" is ${formatMemberFileSize(file.size)} — maximum file size is 1.5 MB.`)
      continue
    }
    try {
      const url = await readFileAsDataUrl(file)
      attachments.push({
        id: `att-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        name: file.name || 'Document',
        url,
        filePath: null,
        size: file.size,
        uploadedAt: new Date().toISOString(),
        uploadedBy: uploadedBy != null ? String(uploadedBy) : null,
      })
    } catch (e) {
      errors.push(`Could not read "${file.name}".`)
    }
  }
  return { attachments, errors }
}

export function normalizeResponsibilityText(text) {
  if (!text) return ''
  const trimmed = String(text).trim()
  if (!trimmed) return ''
  const upper = trimmed.toUpperCase()
  if (upper === 'NA' || upper === 'N/A') return ''
  return trimmed.toLowerCase()
}

export function isValidResponsibilityNote(description) {
  return !!normalizeResponsibilityText(description)
}

/** Derive a short step title from legacy description-only data. */
export function deriveResponsibilityTitleFromDescription(description) {
  const text = String(description || '').trim()
  if (!text) return ''
  const firstLine = text.split(/\r?\n/)[0].trim()
  if (firstLine.length <= 60) return firstLine
  return `${firstLine.slice(0, 60).trim()}…`
}

export function getMemberResponsibilityTitle(member) {
  if (!member) return ''
  const explicit = member.responsibilityTitle ?? member.responsibility_title
  if (isValidResponsibilityNote(explicit)) return String(explicit).trim()
  return deriveResponsibilityTitleFromDescription(
    member.responsibilityDescription ?? member.responsibility_description,
  )
}

export function getMemberResponsibilityDescription(member) {
  if (!member) return ''
  return String(member.responsibilityDescription ?? member.responsibility_description ?? '').trim()
}

/** Member has an assignable responsibility when title or description is valid. */
export function hasValidMemberResponsibility(member) {
  return isValidResponsibilityNote(getMemberResponsibilityTitle(member))
    || isValidResponsibilityNote(getMemberResponsibilityDescription(member))
}

/** Whether description adds detail beyond the step title (avoids duplicate display). */
export function responsibilityDescriptionDiffersFromTitle(title, description) {
  const t = normalizeResponsibilityText(title)
  const d = normalizeResponsibilityText(description)
  return !!d && d !== t
}

export function generateResponsibilityGroupId() {
  return `rg-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

/** Stable group key: explicit group id wins, else normalized responsibility text. */
export function getResponsibilityGroupKey(member) {
  if (!member) return null
  const groupId = member.responsibilityGroupId ?? member.responsibility_group_id
  if (groupId) return `group:${groupId}`
  const normalized = normalizeResponsibilityText(
    member.responsibilityDescription ?? member.responsibility_description,
  )
  if (!normalized) return null
  return `text:${normalized}`
}

function memberResponsibilityOrderValue(member) {
  const raw = member?.responsibilityOrder ?? member?.responsibility_order
  if (raw == null || raw === '') return null
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
}

/** Parse `rg-{timestamp}-…` group ids for creation-order fallback. */
function responsibilityGroupCreatedAt(member) {
  const groupId = member?.responsibilityGroupId ?? member?.responsibility_group_id
  if (!groupId) return null
  const match = String(groupId).match(/^rg-(\d+)-/)
  if (!match) return null
  const ts = Number(match[1])
  return Number.isFinite(ts) ? ts : null
}

/**
 * Group members with valid responsibility notes for step ordering.
 * Sort key: explicit responsibilityOrder, else group id timestamp, else member index.
 */
export function getResponsibilityGroupEntries(members) {
  const list = normalizeMembers(members)
  const withNotes = list
    .map((member, originalIndex) => ({ member, originalIndex }))
    .filter(({ member }) => hasValidMemberResponsibility(member))

  const groups = new Map()
  for (const item of withNotes) {
    const key = getResponsibilityGroupKey(item.member) || `solo:${item.member.empId}`
    if (!groups.has(key)) groups.set(key, [])
    groups.get(key).push(item)
  }

  return Array.from(groups.entries()).map(([key, items]) => {
    const explicitOrders = items
      .map(({ member }) => memberResponsibilityOrderValue(member))
      .filter(o => o != null)
    const groupTimestamps = items
      .map(({ member }) => responsibilityGroupCreatedAt(member))
      .filter(ts => ts != null)
    const sortOrder = explicitOrders.length
      ? Math.min(...explicitOrders)
      : groupTimestamps.length
        ? Math.min(...groupTimestamps)
        : Math.min(...items.map(i => i.originalIndex))
    return { key, items, sortOrder }
  }).sort((a, b) => {
    if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder
    return a.key.localeCompare(b.key)
  })
}

/** Next step order when adding a new responsibility group. */
export function getNextResponsibilityOrder(membersOrResponsibilities) {
  if (Array.isArray(membersOrResponsibilities) && membersOrResponsibilities[0]?.assignees) {
    const list = membersOrResponsibilities
    if (!list.length) return Date.now()
    const maxSortOrder = Math.max(...list.map(r => Number(r.responsibilityOrder) || 0))
    return maxSortOrder + 1
  }
  const entries = getResponsibilityGroupEntries(membersOrResponsibilities)
  if (!entries.length) return Date.now()
  const maxSortOrder = Math.max(...entries.map(e => e.sortOrder))
  return maxSortOrder + 1
}

/** Resolve persisted/inferred order for an existing responsibility group. */
export function resolveResponsibilityGroupOrder(members, referenceMember) {
  const groupKey = getResponsibilityGroupKey(referenceMember)
  if (!groupKey) return getNextResponsibilityOrder(members)
  const entry = getResponsibilityGroupEntries(members).find(e => e.key === groupKey)
  return entry ? entry.sortOrder : getNextResponsibilityOrder(members)
}

/** Normalize persisted project.responsibilities rows. */
export function normalizeResponsibilities(responsibilities) {
  if (!Array.isArray(responsibilities)) return []
  return responsibilities.map((r, i) => {
    const id = String(r.id ?? r.responsibilityGroupId ?? r.responsibility_group_id ?? `rg-legacy-${i}`)
    const title = String(r.responsibilityTitle ?? r.responsibility_title ?? '').trim()
    const description = String(r.responsibilityDescription ?? r.responsibility_description ?? '').trim()
    const resolvedTitle = isValidResponsibilityNote(title)
      ? title
      : deriveResponsibilityTitleFromDescription(description)
    const assigneesRaw = Array.isArray(r.assignees) ? r.assignees : []
    const assignees = assigneesRaw.map(a => ({
      empId: String(a.empId ?? a.emp_id ?? ''),
      name: a.name ? String(a.name).trim() : null,
      completed: !!(a.completed ?? a.responsibilityCompleted ?? a.responsibility_completed),
      completedAt: a.completedAt ?? a.completed_at ?? a.responsibilityCompletedAt ?? null,
      sharedFromEmpId: a.sharedFromEmpId ?? a.shared_from_emp_id ?? null,
    })).filter(a => a.empId)
    const orderRaw = r.responsibilityOrder ?? r.responsibility_order
    const orderNum = Number(orderRaw)
    return {
      id,
      responsibilityTitle: resolvedTitle,
      responsibilityDescription: description,
      responsibilityOrder: Number.isFinite(orderNum) ? orderNum : i + 1,
      attachments: normalizeMemberAttachments(r.attachments),
      assignees,
    }
  }).filter(r => (
    r.assignees.length
    && (isValidResponsibilityNote(r.responsibilityTitle) || isValidResponsibilityNote(r.responsibilityDescription))
  ))
}

/** Build responsibility items from legacy per-member fields. */
export function synthesizeResponsibilitiesFromMembers(members) {
  return getResponsibilityGroupEntries(members).map((entry, index) => {
    entry.items.sort((a, b) => a.originalIndex - b.originalIndex)
    const primary = entry.items[0].member
    const stableId = primary.responsibilityGroupId
      || `legacy-${String(getResponsibilityGroupKey(primary) || `solo-${primary.empId}`).replace(/[^a-zA-Z0-9_-]/g, '-')}`
    return {
      id: stableId,
      responsibilityTitle: getMemberResponsibilityTitle(primary),
      responsibilityDescription: getMemberResponsibilityDescription(primary),
      responsibilityOrder: entry.sortOrder != null ? entry.sortOrder : index + 1,
      attachments: normalizeMemberAttachments(primary.attachments),
      assignees: entry.items.map(({ member }) => ({
        empId: String(member.empId),
        completed: !!member.responsibilityCompleted,
        completedAt: member.responsibilityCompletedAt || null,
        sharedFromEmpId: member.sharedFromEmpId || null,
      })),
    }
  })
}

/**
 * Source of truth for steps: project.responsibilities when present,
 * otherwise synthesize from member responsibility fields (legacy).
 */
export function getProjectResponsibilities(project) {
  if (!project) return []
  if (Array.isArray(project.responsibilities) && project.responsibilities.length) {
    return normalizeResponsibilities(project.responsibilities)
  }
  return synthesizeResponsibilitiesFromMembers(project.members)
}

/** Merge API + computed members by empId — never drop a team member the API omitted. */
export function mergeMembersUnion(apiMembers, computedMembers) {
  const byId = new Map()
  const add = m => {
    const id = String(m.empId ?? m.emp_id ?? '')
    if (!id) return
    const prev = byId.get(id)
    byId.set(id, prev
      ? {
        ...prev,
        ...m,
        name: m.name || prev.name,
        email: m.email != null ? m.email : prev.email,
      }
      : m)
  }
  normalizeMembers(apiMembers).forEach(add)
  normalizeMembers(computedMembers).forEach(add)
  return Array.from(byId.values())
}

/**
 * Ensure every responsibility assignee exists in members with denormalized fields.
 * Backend often persists assignees in responsibilities but omits them from members JSONB.
 */
export function reconcileProjectMembers(project, memberDefaults = {}) {
  if (!project) return project
  const responsibilities = getProjectResponsibilities(project)
  const defaultsFromAssignees = { ...memberDefaults }
  for (const resp of responsibilities) {
    for (const assignee of resp.assignees || []) {
      const empId = String(assignee.empId ?? assignee.emp_id ?? '')
      if (!empId || defaultsFromAssignees[empId]?.name) continue
      if (assignee.name) {
        defaultsFromAssignees[empId] = { name: assignee.name }
      }
    }
  }
  let members = ensureCreatorInMembers(project.members, project)
  members = ensureMembersForAssignees(
    members,
    responsibilities,
    defaultsFromAssignees,
  )
  members = syncMembersFromResponsibilities(members, responsibilities)
  return {
    ...project,
    members,
    responsibilities,
  }
}

/**
 * Append project members for every responsibility assignee not already on the team.
 * Legacy PUT payloads only persist `members` JSON — assignees must exist there.
 */
export function ensureMembersForAssignees(members, responsibilities, memberDefaults = {}) {
  const list = [...normalizeMembers(members)]
  const byId = new Map(list.map(m => [String(m.empId), m]))

  for (const resp of responsibilities || []) {
    for (const assignee of resp.assignees || []) {
      const empId = String(assignee.empId ?? assignee.emp_id ?? '')
      if (!empId || byId.has(empId)) continue
      const defaults = memberDefaults[empId] || {}
      const added = {
        empId,
        name: defaults.name || empId,
        email: defaults.email ?? null,
        role: defaults.role || PROJECT_ROLES.CONTRIBUTOR,
        responsibilityTitle: '',
        responsibilityDescription: '',
        responsibilityGroupId: null,
        sharedFromEmpId: null,
        attachments: [],
        addedAt: defaults.addedAt || new Date().toISOString(),
        addedBy: defaults.addedBy ?? null,
      }
      list.push(added)
      byId.set(empId, added)
    }
  }

  return list
}

/** Keep member denormalized fields aligned with their earliest responsibility. */
export function syncMembersFromResponsibilities(members, responsibilities) {
  const list = normalizeMembers(members)
  const sorted = [...(responsibilities || [])].sort(
    (a, b) => (a.responsibilityOrder - b.responsibilityOrder) || String(a.id).localeCompare(String(b.id)),
  )
  const primaryByEmp = new Map()
  for (const resp of sorted) {
    for (const assignee of resp.assignees || []) {
      const key = String(assignee.empId)
      if (!primaryByEmp.has(key)) {
        primaryByEmp.set(key, { resp, assignee })
      }
    }
  }
  return list.map(m => {
    const primary = primaryByEmp.get(String(m.empId))
    if (!primary) {
      return {
        ...m,
        responsibilityTitle: '',
        responsibilityDescription: '',
        responsibilityCompleted: false,
        responsibilityCompletedAt: null,
        responsibilityGroupId: null,
        responsibilityOrder: null,
        sharedFromEmpId: null,
        attachments: [],
      }
    }
    const { resp, assignee } = primary
    return {
      ...m,
      responsibilityTitle: resp.responsibilityTitle,
      responsibilityDescription: resp.responsibilityDescription,
      responsibilityCompleted: !!assignee.completed,
      responsibilityCompletedAt: assignee.completedAt || null,
      responsibilityGroupId: resp.id,
      responsibilityOrder: resp.responsibilityOrder,
      sharedFromEmpId: assignee.sharedFromEmpId || null,
      attachments: normalizeMemberAttachments(resp.attachments),
    }
  })
}

/**
 * Numbered responsibility steps — one row per assignee on each responsibility.
 */
export function buildResponsibilitySteps(project) {
  const normalized = project || {}
  const members = normalizeMembers(normalized.members)
  const memberById = new Map(members.map(m => [String(m.empId), m]))
  const responsibilities = getProjectResponsibilities(normalized)
    .slice()
    .sort((a, b) => (a.responsibilityOrder - b.responsibilityOrder) || String(a.id).localeCompare(String(b.id)))

  const steps = []
  responsibilities.forEach((resp, index) => {
    const stepNumber = index + 1
    for (const assignee of resp.assignees) {
      const assigneeEmpId = String(assignee.empId)
      const member = memberById.get(assigneeEmpId)
      if (!member) {
        steps.push({
          empId: assigneeEmpId,
          name: assignee.name || assigneeEmpId,
          email: null,
          role: PROJECT_ROLES.CONTRIBUTOR,
          responsibilityTitle: resp.responsibilityTitle,
          responsibilityDescription: resp.responsibilityDescription,
          responsibilityCompleted: !!assignee.completed,
          responsibilityCompletedAt: assignee.completedAt || null,
          responsibilityGroupId: resp.id,
          responsibilityOrder: resp.responsibilityOrder,
          sharedFromEmpId: assignee.sharedFromEmpId || null,
          attachments: resp.attachments || [],
          stepNumber,
        })
        continue
      }
      steps.push({
        ...member,
        responsibilityTitle: resp.responsibilityTitle,
        responsibilityDescription: resp.responsibilityDescription,
        responsibilityCompleted: !!assignee.completed,
        responsibilityCompletedAt: assignee.completedAt || null,
        responsibilityGroupId: resp.id,
        responsibilityOrder: resp.responsibilityOrder,
        sharedFromEmpId: assignee.sharedFromEmpId || null,
        attachments: resp.attachments || [],
        stepNumber,
      })
    }
  })
  return steps
}

/**
 * Ensure project creator exists in members as PM — backend often persists
 * createdBy without duplicating the creator in members JSONB.
 */
export function ensureCreatorInMembers(members, project) {
  const creator = project?.createdBy ?? project?.created_by
  const list = normalizeMembers(members)
  if (creator == null) return list
  const creatorId = String(creator)
  if (list.some(m => String(m.empId) === creatorId)) return list
  const name = project.createdByName ?? project.created_by_name ?? creatorId
  return [{
    empId: creatorId,
    name,
    email: null,
    role: PROJECT_ROLES.PROJECT_MANAGER,
    responsibilityTitle: '',
    responsibilityDescription: '',
    responsibilityCompleted: false,
    responsibilityCompletedAt: null,
    responsibilityGroupId: null,
    responsibilityOrder: null,
    sharedFromEmpId: null,
    attachments: [],
    addedAt: project.createdAt ?? project.created_at ?? new Date().toISOString(),
    addedBy: creatorId,
  }, ...list]
}

export function normalizeMembers(members) {
  if (!Array.isArray(members)) return []
  return members.map(m => ({
    empId: String(m.empId ?? m.emp_id ?? m.id ?? ''),
    name: m.name || m.title || m.empId || 'Unknown',
    email: m.email || null,
    role: normalizeProjectRole(m.role),
    responsibilityTitle: (() => {
      const rawTitle = m.responsibilityTitle ?? m.responsibility_title ?? ''
      const desc = m.responsibilityDescription ?? m.responsibility_description ?? ''
      if (isValidResponsibilityNote(rawTitle)) return String(rawTitle).trim()
      if (isValidResponsibilityNote(desc)) return deriveResponsibilityTitleFromDescription(desc)
      return ''
    })(),
    responsibilityDescription: m.responsibilityDescription ?? m.responsibility_description ?? '',
    responsibilityCompleted: !!(m.responsibilityCompleted ?? m.responsibility_completed),
    responsibilityCompletedAt: m.responsibilityCompletedAt ?? m.responsibility_completed_at ?? null,
    responsibilityGroupId: m.responsibilityGroupId ?? m.responsibility_group_id ?? null,
    responsibilityOrder: memberResponsibilityOrderValue(m),
    sharedFromEmpId: m.sharedFromEmpId ?? m.shared_from_emp_id ?? null,
    attachments: normalizeMemberAttachments(m.attachments),
    addedAt: m.addedAt || m.added_at || null,
    addedBy: m.addedBy ?? m.added_by ?? null,
  })).filter(m => m.empId)
}

/**
 * Team size shown in UI — matches ProjectTeamPanel list (no +1 for creator when
 * already included in members, which is the case for newly created projects).
 */
export function projectMemberCount(project) {
  if (!project) return 0
  const members = normalizeMembers(project.members)
  return new Set(members.map(m => String(m.empId))).size
}

export function getMemberRole(project, empId) {
  if (!project || empId == null) return null
  const members = normalizeMembers(project.members)
  const member = members.find(m => String(m.empId) === String(empId))
  return member ? member.role : null
}

/** Clone responsibility fields from one member for sharing with others. */
export function copyMemberResponsibility(sourceMember, { linkToSource = false } = {}) {
  if (!sourceMember) {
    return { responsibilityTitle: '', responsibilityDescription: '', attachments: [] }
  }
  const description = sourceMember.responsibilityDescription ?? sourceMember.responsibility_description ?? ''
  const title = getMemberResponsibilityTitle(sourceMember)
  const attachments = normalizeMemberAttachments(sourceMember.attachments).map(a => ({ ...a }))
  const result = { responsibilityTitle: title, responsibilityDescription: description, attachments }
  if (linkToSource && sourceMember.empId) {
    result.sharedFromEmpId = String(sourceMember.empId)
    if (sourceMember.responsibilityGroupId) {
      result.responsibilityGroupId = sourceMember.responsibilityGroupId
    }
  }
  const order = memberResponsibilityOrderValue(sourceMember)
  if (order != null) result.responsibilityOrder = order
  return result
}

/**
 * Share an existing responsibility with more assignees (project.responsibilities).
 * Reads via getProjectResponsibilities (synthesizes from legacy member fields when
 * project.responsibilities is absent).
 */
export function applyResponsibilityToProject(project, sourceEmpId, targetEmpIds, responsibilityOverride = null) {
  const members = normalizeMembers(project?.members)
  const source = members.find(m => String(m.empId) === String(sourceEmpId))
  if (!source && !responsibilityOverride) {
    return { members, responsibilities: getProjectResponsibilities(project) }
  }

  const copied = responsibilityOverride
    ? {
      responsibilityTitle: responsibilityOverride.responsibilityTitle ?? '',
      responsibilityDescription: responsibilityOverride.responsibilityDescription ?? '',
      attachments: normalizeMemberAttachments(responsibilityOverride.attachments),
    }
    : copyMemberResponsibility(source)

  const targets = [...new Set(
    (targetEmpIds || [])
      .map(id => String(id))
      .filter(id => id && id !== String(sourceEmpId)),
  )]
  if (!targets.length) {
    return { members, responsibilities: getProjectResponsibilities(project) }
  }

  const responsibilities = getProjectResponsibilities(project)
  const sourceId = String(sourceEmpId)
  const groupId = source?.responsibilityGroupId
    || responsibilityOverride?.responsibilityGroupId
    || generateResponsibilityGroupId()
  let found = false
  const nextResponsibilities = responsibilities.map(resp => {
    if (String(resp.id) !== String(groupId)) return resp
    found = true
    const existingIds = new Set(resp.assignees.map(a => String(a.empId)))
    const added = targets
      .filter(id => !existingIds.has(String(id)))
      .map(empId => ({
        empId: String(empId),
        completed: false,
        completedAt: null,
        sharedFromEmpId: sourceId,
      }))
    return {
      ...resp,
      responsibilityTitle: copied.responsibilityTitle || resp.responsibilityTitle,
      responsibilityDescription: copied.responsibilityDescription || resp.responsibilityDescription,
      attachments: copied.attachments?.length
        ? copied.attachments.map(a => ({ ...a }))
        : resp.attachments,
      assignees: [...resp.assignees, ...added],
    }
  })

  if (!found) {
    const groupOrder = source
      ? resolveResponsibilityGroupOrder(members, source)
      : getNextResponsibilityOrder(responsibilities)
    nextResponsibilities.push({
      id: groupId,
      responsibilityTitle: String(copied.responsibilityTitle || '').trim(),
      responsibilityDescription: String(copied.responsibilityDescription || '').trim(),
      responsibilityOrder: groupOrder,
      attachments: normalizeMemberAttachments(copied.attachments),
      assignees: [
        {
          empId: sourceId,
          completed: !!source?.responsibilityCompleted,
          completedAt: source?.responsibilityCompletedAt || null,
          sharedFromEmpId: null,
        },
        ...targets.map(empId => ({
          empId: String(empId),
          completed: false,
          completedAt: null,
          sharedFromEmpId: sourceId,
        })),
      ],
    })
  }

  const membersWithAssignees = ensureMembersForAssignees(members, nextResponsibilities)

  return {
    responsibilities: nextResponsibilities,
    members: syncMembersFromResponsibilities(membersWithAssignees, nextResponsibilities),
  }
}

/**
 * Append a new responsibility step without replacing existing ones.
 * Same person may appear on multiple steps.
 */
export function appendResponsibilityToProject(project, targetEmpIds, {
  responsibilityTitle,
  responsibilityDescription,
  attachments = [],
  memberDefaults = {},
} = {}) {
  const members = normalizeMembers(project?.members)
  const desc = String(responsibilityDescription || '').trim()
  const title = String(responsibilityTitle || '').trim()
    || deriveResponsibilityTitleFromDescription(desc)
  if (!isValidResponsibilityNote(title) && !isValidResponsibilityNote(desc)) {
    return {
      members,
      responsibilities: getProjectResponsibilities(project),
    }
  }

  const targets = [...new Set(
    (targetEmpIds || [])
      .map(id => String(id))
      .filter(Boolean),
  )]
  if (!targets.length) {
    return {
      members,
      responsibilities: getProjectResponsibilities(project),
    }
  }

  const existing = getProjectResponsibilities(project)
  const groupId = generateResponsibilityGroupId()
  const groupOrder = getNextResponsibilityOrder(existing)
  const primaryId = targets[0]
  const nextResponsibilities = [
    ...existing,
    {
      id: groupId,
      responsibilityTitle: title,
      responsibilityDescription: desc,
      responsibilityOrder: groupOrder,
      attachments: normalizeMemberAttachments(attachments).map(a => ({ ...a })),
      assignees: targets.map(empId => {
        const id = String(empId)
        const defaults = memberDefaults[id] || {}
        return {
          empId: id,
          name: defaults.name || null,
          completed: false,
          completedAt: null,
          sharedFromEmpId: id === primaryId ? null : primaryId,
        }
      }),
    },
  ]

  const membersWithAssignees = ensureMembersForAssignees(
    members,
    nextResponsibilities,
    memberDefaults,
  )

  return {
    responsibilities: nextResponsibilities,
    members: syncMembersFromResponsibilities(membersWithAssignees, nextResponsibilities),
  }
}

/** Patch a responsibility / assignee from a member update payload. */
export function applyMemberUpdateToResponsibilities(project, memberUpdate) {
  const members = normalizeMembers(project?.members)
  const empId = String(memberUpdate.empId ?? memberUpdate.emp_id ?? '')
  if (!empId) {
    return { members, responsibilities: getProjectResponsibilities(project) }
  }

  let responsibilities = getProjectResponsibilities(project)
    .slice()
    .sort((a, b) => (a.responsibilityOrder - b.responsibilityOrder) || String(a.id).localeCompare(String(b.id)))
  const groupId = memberUpdate.responsibilityGroupId ?? memberUpdate.responsibility_group_id
  const hasRespFields = (
    memberUpdate.responsibilityTitle != null
    || memberUpdate.responsibilityDescription != null
    || memberUpdate.responsibilityCompleted != null
    || memberUpdate.attachments != null
  )

  if (hasRespFields && responsibilities.length) {
    let matched = false
    responsibilities = responsibilities.map(resp => {
      const isTargetGroup = groupId != null
        ? String(resp.id) === String(groupId)
        : resp.assignees.some(a => String(a.empId) === empId)
      if (!isTargetGroup) return resp
      // When no groupId, only update the member's earliest matching responsibility once.
      if (groupId == null && matched) return resp
      if (!resp.assignees.some(a => String(a.empId) === empId)) return resp
      matched = true

      const nextAssignees = resp.assignees.map(a => {
        if (String(a.empId) !== empId) return a
        return {
          ...a,
          completed: memberUpdate.responsibilityCompleted != null
            ? !!memberUpdate.responsibilityCompleted
            : a.completed,
          completedAt: memberUpdate.responsibilityCompletedAt !== undefined
            ? memberUpdate.responsibilityCompletedAt
            : a.completedAt,
        }
      })

      return {
        ...resp,
        responsibilityTitle: memberUpdate.responsibilityTitle != null
          ? String(memberUpdate.responsibilityTitle).trim()
          : resp.responsibilityTitle,
        responsibilityDescription: memberUpdate.responsibilityDescription != null
          ? String(memberUpdate.responsibilityDescription).trim()
          : resp.responsibilityDescription,
        attachments: memberUpdate.attachments != null
          ? normalizeMemberAttachments(memberUpdate.attachments)
          : resp.attachments,
        assignees: nextAssignees,
      }
    })
  }

  const mergedMembers = members.map(m => (
    String(m.empId) === empId ? mergeMemberUpdate(m, memberUpdate) : m
  ))
  const synced = responsibilities.length
    ? syncMembersFromResponsibilities(mergedMembers, responsibilities)
    : mergedMembers

  // Preserve non-responsibility field edits (role/name/email) after sync.
  const withRoleEdits = synced.map(m => {
    if (String(m.empId) !== empId) return m
    return {
      ...m,
      role: memberUpdate.role || m.role,
      name: memberUpdate.name || m.name,
      email: memberUpdate.email != null ? memberUpdate.email : m.email,
    }
  })

  return { members: withRoleEdits, responsibilities }
}

function mergeMemberUpdate(existing, incoming) {
  return {
    ...existing,
    role: incoming.role || existing.role,
    name: incoming.name || existing.name,
    email: incoming.email != null ? incoming.email : existing.email,
    responsibilityTitle: incoming.responsibilityTitle != null
      ? incoming.responsibilityTitle
      : existing.responsibilityTitle,
    responsibilityDescription: incoming.responsibilityDescription != null
      ? incoming.responsibilityDescription
      : existing.responsibilityDescription,
    responsibilityCompleted: incoming.responsibilityCompleted != null
      ? incoming.responsibilityCompleted
      : existing.responsibilityCompleted,
    responsibilityCompletedAt: incoming.responsibilityCompletedAt != null
      ? incoming.responsibilityCompletedAt
      : existing.responsibilityCompletedAt,
    attachments: incoming.attachments != null
      ? incoming.attachments
      : existing.attachments,
    responsibilityGroupId: incoming.responsibilityGroupId != null
      ? incoming.responsibilityGroupId
      : existing.responsibilityGroupId,
    responsibilityOrder: incoming.responsibilityOrder != null
      ? incoming.responsibilityOrder
      : existing.responsibilityOrder,
    sharedFromEmpId: incoming.sharedFromEmpId != null
      ? incoming.sharedFromEmpId
      : existing.sharedFromEmpId,
  }
}

export function buildProjectPayload(form, userData) {
  const empId = userData?.emp_id ?? userData?.empId ?? null
  const displayName = `${userData?.first_name || ''} ${userData?.last_name || ''}`.trim()
    || userData?.title
    || 'Unknown'
  const now = new Date().toISOString()
  return {
    name: (form.name || '').trim(),
    description: form.description || '',
    status: form.status || PROJECT_STATUSES.ACTIVE,
    color: form.color || PROJECT_COLOR_OPTIONS[0],
    createdBy: form.createdBy != null ? String(form.createdBy) : (empId != null ? String(empId) : null),
    createdByName: form.createdByName || displayName,
    members: normalizeMembers(form.members || []),
    updatedAt: now,
    ...(form.id != null ? { id: form.id } : { createdAt: now }),
  }
}
